import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import styles from "./Pages.module.css";

function BackupReadinessReview() {
  return (
    <section className={styles.pageSection}>
      <Helmet>
        <title>$149 IT Health Check | Tharpe Industries</title>
        <meta
          name="description"
          content="$149 remote IT Health Check for Shreveport-Bossier small businesses. Review backups, security, Wi‑Fi, and core setup basics, then get a plain-English PDF report with prioritized recommendations."
        />
      </Helmet>

      <h2>$149 IT Health Check</h2>
      <p>
        Slow computer? Spotty Wi‑Fi? Worried about backups? You need a clear picture of what's actually broken, what's working, and what to fix first, without getting pushed into a big project or overwhelmed by tech jargon.
      </p>
      <p>
        For $149, you get a 60–90 minute remote session where we review your setup, identify weak spots, and answer the questions most business owners don't know to ask. At the end, you get a plain-English PDF report with a prioritized action list, no obligation to do anything with it. Just honest answers.
      </p>

      <div className={styles.contentContainer}>
        <div className={styles.textContent}>
          <h3>What&apos;s included</h3>
          <ul>
            <li><FaCheckCircle aria-hidden="true" /> <strong>Backup status check:</strong> Do you have one? Is it actually working? Can you actually recover if something breaks?</li>
            <li><FaCheckCircle aria-hidden="true" /> <strong>Basic security review:</strong> Antivirus status, Windows updates, MFA setup, remote access exposure, and account practices</li>
            <li><FaCheckCircle aria-hidden="true" /> <strong>Network & Wi‑Fi assessment:</strong> Speed checks, equipment condition, coverage issues, and basic configuration review</li>
            <li><FaCheckCircle aria-hidden="true" /> <strong>Password & account security basics:</strong> How accounts are managed, shared passwords, and account best practices</li>
            <li><FaCheckCircle aria-hidden="true" /> <strong>Plain-English PDF report:</strong> Clear status (green / yellow / red), what's working, what needs attention, and prioritized next steps</li>
            <li><FaCheckCircle aria-hidden="true" /> <strong>No obligation:</strong> The report is yours to keep. You decide what to do next, I don't push.</li>
          </ul>

          <h3>What happens after</h3>
          <p>
            After the review, you get the report and decide what you want to do next. If you want help fixing anything, we can talk about that separately. If not, you still leave with a clearer picture of where things stand. You keep the report regardless.
          </p>

          <h3>This is for you if...</h3>
          <ul>
            <li>You've never had anyone look at your IT setup</li>
            <li>You're not sure if your backups actually work</li>
            <li>You want an honest second opinion before signing up for ongoing support</li>
            <li>You want to know what to fix without a sales pitch or pressure to buy something</li>
            <li>You're concerned about security but don't know where to start</li>
          </ul>

          <h3>No sales pitch</h3>
          <p>
            This is not a bait-and-switch consultation. The goal is to give you a useful, honest read on your setup and explain it clearly.
          </p>

          <div className={styles.ctaContainer}>
            <p><strong>$149 fixed starting price</strong></p>
            <p className={styles.emergencyText}>Remote-only. No need to come in or wait for a tech on-site. Scoped to fit into a practical 1–2 hour session.</p>
            <Link to="/contact-us" className={styles.ctaButton}>Book the IT Health Check</Link>
            <p className={styles.emergencyText} style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
              ✓ No obligation to sign up for anything else<br/>
              ✓ Done completely remotely<br/>
              ✓ You keep the report regardless of next steps
            </p>
          </div>

          <div className={styles.relatedPages}>
            <p className={styles.relatedPagesHeading}>Related services:</p>
            <div className={styles.relatedPageLinks}>
              <Link to="/managed-it-services">Managed IT Plans</Link>
              <Link to="/computer-repair">Computer Repair</Link>
              <Link to="/cybersecurity">Cybersecurity</Link>
              <Link to="/data-backup-recovery">Data Backup & Recovery</Link>
              <Link to="/how-to-choose-it-support">How to Choose IT Support</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default BackupReadinessReview;
